/*
 * The capability chapters every organization guide shares, in order. A
 * chapter can carry notes for particular audiences:
 *
 *   :::for universities companies
 *   > **At a university:** …
 *   :::
 *
 * `sharedChapters(audience)` keeps the notes for that audience and drops the
 * rest; the general guide (audience null) drops them all.
 */
import atAGlance from './01-at-a-glance.md?raw';
import gettingStarted from './02-getting-started.md?raw';
import setUp from './03-set-up.md?raw';
import people from './04-people.md?raw';
import readyMade from './05-ready-made.md?raw';
import makeItYours from './06-make-it-yours.md?raw';
import buildCourse from './07-build-a-course.md?raw';
import assessments from './08-assessments.md?raw';
import events from './09-events.md?raw';
import assign from './10-assign.md?raw';
import spread from './11-spread-the-word.md?raw';
import impact from './12-impact.md?raw';
import support from './13-support.md?raw';
import trust from './14-trust.md?raw';
import appendix from './16-checklists-faq.md?raw';
import type { GuideAudience, GuideChapter } from '../types';

const SHARED = {
  'at-a-glance': {
    title: 'Capabilities at a glance',
    summary: 'Everything you can do, on one page',
    markdown: atAGlance,
  },
  'getting-started': {
    title: 'Getting started',
    summary: 'Request your organization and sign in',
    markdown: gettingStarted,
  },
  'set-up': {
    title: 'Set up your organization',
    summary: 'Profile, structure and email domains',
    markdown: setUp,
  },
  people: {
    title: 'Bring people in',
    summary: 'Invite codes, posters, email invitations, roles and groups',
    markdown: people,
  },
  'ready-made': {
    title: 'Start with ready-made content',
    summary: 'The library, learning paths and the placement quiz',
    markdown: readyMade,
  },
  'make-it-yours': {
    title: 'Make it yours',
    summary: 'Customize or copy library content, and choose who sees it',
    markdown: makeItYours,
  },
  'build-a-course': {
    title: 'Build your own course',
    summary: 'From outline to published course with a certificate',
    markdown: buildCourse,
  },
  assessments: {
    title: 'Assessments and challenges',
    summary: 'Question banks, quizzes and campaign challenges',
    markdown: assessments,
  },
  events: {
    title: 'Workshops and webinars',
    summary: 'Live sessions, registration, check-in and certificates',
    markdown: events,
  },
  assign: {
    title: 'Assign and remind',
    summary: 'Courses with due dates and automatic reminders',
    markdown: assign,
  },
  'spread-the-word': {
    title: 'Spread the word',
    summary: 'Share cards, posters and certificates that invite others',
    markdown: spread,
  },
  impact: {
    title: 'See the impact',
    summary: 'Reports, engagement and learner profiles',
    markdown: impact,
  },
  support: {
    title: 'Support your learners',
    summary: 'Feedback, help requests and the daily digest',
    markdown: support,
  },
  trust: {
    title: 'Trust and privacy',
    summary: 'How people, records and files are protected',
    markdown: trust,
  },
  'checklists-faq': {
    title: 'Checklists and FAQ',
    summary: 'Setup, first course and first event checklists; common questions',
    markdown: appendix,
  },
} satisfies Record<string, Omit<GuideChapter, 'slug'>>;

export type SharedSlug = keyof typeof SHARED;

const NOTE = /^:::for ([a-z ]+)\n([\s\S]*?)^:::[ \t]*$\n?/gm;

/** A chapter's Markdown with only `audience`'s notes kept. */
export function tailor(markdown: string, audience: GuideAudience | null): string {
  return markdown
    .replace(NOTE, (_, who: string, body: string) =>
      audience && who.split(' ').includes(audience) ? body : '',
    )
    .replace(/\n{3,}/g, '\n\n');
}

/** The shared chapters by slug, with `audience`'s notes. */
export function sharedChapters(audience: GuideAudience | null): Record<SharedSlug, GuideChapter> {
  return Object.fromEntries(
    Object.entries(SHARED).map(([slug, c]) => [
      slug,
      { ...c, slug, markdown: tailor(c.markdown, audience) },
    ]),
  ) as Record<SharedSlug, GuideChapter>;
}
